import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';
import { useScopeKey } from './useScopeKey.js';

// ── Param builder ─────────────────────────────────────────────────────────────

function buildParams(obj) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, v);
  }
  const str = params.toString();
  return str ? `?${str}` : '';
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * KPIs agrégés pour une période.
 * { from, to } — `to` est exclusif côté API (≡ < to dans le SQL).
 */
export function useSalesSummary({ from, to } = {}) {
  const scopeKey = useScopeKey();
  return useQuery({
    queryKey: ['sales', 'summary', scopeKey, { from, to }],
    queryFn: () => api.get(`/sales/summary${buildParams({ from, to })}`),
    staleTime: 60_000,
    enabled: scopeKey !== 'loading' && Boolean(from) && Boolean(to),
  });
}

/**
 * Série temporelle CA/commandes par bucket.
 * granularity : 'day' | 'week' | 'month'
 */
export function useSalesByPeriod({ from, to, granularity } = {}) {
  const scopeKey = useScopeKey();
  return useQuery({
    queryKey: ['sales', 'period', scopeKey, { from, to, granularity }],
    queryFn: () => api.get(`/sales/by-period${buildParams({ from, to, granularity })}`),
    staleTime: 60_000,
    enabled: scopeKey !== 'loading' && Boolean(from) && Boolean(to) && Boolean(granularity),
  });
}

/**
 * CA lignes et pièces vendues par lot d'origine (SKU LT<n>), trié DESC.
 */
export function useSalesByLot({ from, to } = {}) {
  const scopeKey = useScopeKey();
  return useQuery({
    queryKey: ['sales', 'lot', scopeKey, { from, to }],
    queryFn: () => api.get(`/sales/by-lot${buildParams({ from, to })}`),
    staleTime: 60_000,
    enabled: scopeKey !== 'loading' && Boolean(from) && Boolean(to),
  });
}

/**
 * Ventilation géographique.
 * level : 'country' | 'province' | 'city'
 * country : code ISO-2 (requis si level !== 'country')
 * province : nom de région (optionnel pour level=city)
 */
export function useSalesByLocation({ from, to, level, country, province } = {}) {
  const scopeKey = useScopeKey();
  return useQuery({
    queryKey: ['sales', 'location', scopeKey, { from, to, level, country, province }],
    queryFn: () =>
      api.get(`/sales/by-location${buildParams({ from, to, level, country, province })}`),
    staleTime: 60_000,
    enabled: scopeKey !== 'loading' && Boolean(from) && Boolean(to) && Boolean(level),
  });
}

/**
 * Mutation de synchronisation des commandes Shopify.
 * Invalide tout le cache ['sales'] au succès.
 * mutate({ backfill: true }) pour un backfill historique.
 */
export function useSyncShopifyOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ backfill = false } = {}) =>
      api.post(`/integrations/shopify/orders/sync${backfill ? '?backfill=true' : ''}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales'] });
    },
  });
}
